import * as React from "react";
import { ArrowUp, Square } from "lucide-react";
import { cn } from "../../lib/cn";

/**
 * Composer.SendButton — single accent CTA at the bottom-right.
 *
 * States:
 *   - idle (canSend === false): soft warm tint + muted arrow, no shadow, cursor `not-allowed`
 *   - ready (canSend === true): full orange gradient + send-pulse on focus-within
 *   - streaming (streaming === true): swaps to a red square Stop button
 *
 * Mirrors v18 `.send-btn`. The pulse animation fires once on focus-within
 * (via the `peer-focus-within` style on `Composer.Root`) — see Tailwind
 * preset keyframe `send-pulse`.
 *
 * Imperative `onStop` only fires when `streaming === true`. When clicking
 * during streaming we DON'T call form submit — we call `onStop` instead.
 */
export interface ComposerSendButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "type"> {
  /** Whether the textarea has submittable content. */
  canSend?: boolean;
  /** Whether a query is currently streaming (swaps to Stop). */
  streaming?: boolean;
  /** Click handler when streaming === true. */
  onStop?: () => void;
  /** Override the default click — when omitted, native form submit happens. */
  onSend?: () => void;
  labels?: Partial<{ send: string; stop: string }>;
}

const DEFAULT_LABELS = {
  send: "Invia messaggio",
  stop: "Interrompi",
} as const;

export const ComposerSendButton = React.forwardRef<HTMLButtonElement, ComposerSendButtonProps>(
  (
    {
      canSend = false,
      streaming = false,
      onStop,
      onSend,
      labels,
      className,
      disabled,
      ...rest
    },
    ref
  ) => {
    const L = { ...DEFAULT_LABELS, ...labels };
    const isStop = streaming;
    const isDisabled = !isStop && (disabled || !canSend);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isStop) {
        e.preventDefault();
        onStop?.();
        return;
      }
      if (onSend) {
        e.preventDefault();
        onSend();
      }
      // else: native submit via form
    };

    return (
      <button
        ref={ref}
        type={isStop ? "button" : "submit"}
        onClick={handleClick}
        disabled={isDisabled}
        aria-label={isStop ? L.stop : L.send}
        aria-disabled={isDisabled || undefined}
        className={cn(
          // Layout & shape — bumped to 32px to clear WCAG 2.5.8 target
          // size + an invisible ``::before`` adds another 6px hit-area
          // ring so the tap target effectively reaches 44px without
          // shifting layout.
          "relative inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[9px] border-none",
          "before:absolute before:inset-[-6px] before:content-['']",
          // Default orange gradient — overridden when streaming or disabled
          !isStop &&
            "bg-[linear-gradient(180deg,#FF8E42_0%,#FF7A1A_100%)] text-white",
          // Stop state — muted ink gradient (June 1, 2026 polish): the
          // previous saturated red read as a critical alert, but stopping
          // streaming is an everyday pause, not a destructive action.
          // Slate-ink scale keeps it visible without weaponising the eye.
          isStop &&
            "bg-[linear-gradient(180deg,#56544F_0%,#1F1D1A_100%)] text-white",
          // Disabled — light & quiet (Jul 12 2026): a soft warm tint + a
          // muted ink arrow, NO shadow. The orange glow was fighting the
          // light theme when there was nothing to send.
          isDisabled &&
            "pointer-events-none cursor-not-allowed bg-ink-1/5 bg-none text-ink-3 shadow-none",
          // Elevation + interactive transitions (Swiss minimal × Ando warmth).
          // The orange-tinted glow only applies to the LIVE send button
          // (enabled, not streaming) — disabled drops all shadow, stop uses a
          // neutral ink shadow, so neither fights a warm halo.
          !isStop &&
            !isDisabled &&
            "shadow-[0_1px_2px_rgba(0,0,0,0.06),0_2px_8px_-2px_rgba(255,122,26,0.35),inset_0_1px_0_rgba(255,255,255,0.22)]",
          isStop &&
            "shadow-[0_1px_2px_rgba(0,0,0,0.08),0_2px_8px_-2px_rgba(31,29,26,0.30),inset_0_1px_0_rgba(255,255,255,0.10)]",
          "transition-[transform,box-shadow,filter] duration-200 ease-spring-out",
          !isDisabled &&
            !isStop &&
            "hover:-translate-y-px hover:scale-[1.03] hover:shadow-[0_1px_2px_rgba(0,0,0,0.08),0_6px_14px_-3px_rgba(255,122,26,0.45),inset_0_1px_0_rgba(255,255,255,0.28)] hover:brightness-[1.04]",
          !isDisabled && "active:translate-y-0 active:scale-[0.94]",
          // Focus ring matches the gradient family — orange halo on
          // send, neutral ink halo on stop. Mixing them would scream
          // "send" while the user is interrupting a stream.
          !isStop &&
            "focus-visible:shadow-[0_0_0_3px_rgba(255,122,26,0.28),0_2px_8px_-2px_rgba(255,122,26,0.4)] focus-visible:outline-none",
          isStop &&
            "focus-visible:shadow-[0_0_0_3px_rgba(31,29,26,0.30),0_2px_8px_-2px_rgba(31,29,26,0.40)] focus-visible:outline-none",
          // Focus-within pulse — fires once 600ms after card focus
          !isDisabled &&
            !isStop &&
            "[.composer-card:focus-within_&]:animate-send-pulse",
          className
        )}
        {...rest}
      >
        {isStop ? (
          <Square
            size={11}
            strokeWidth={2.5}
            fill="currentColor"
            aria-hidden="true"
          />
        ) : (
          <ArrowUp
            size={14}
            strokeWidth={2.5}
            aria-hidden="true"
            className="group-hover:-translate-y-px transition-transform duration-[260ms] ease-spring-out"
          />
        )}
      </button>
    );
  }
);
ComposerSendButton.displayName = "Composer.SendButton";
